<script lang="ts">
  // Dropdown-Inhalt der TopBar-Suche. Zwei Modi:
  //   - Leerer Query: Spalten-Layout mit den Navi-Bloecken (gleiche
  //     Hierarchie wie das alte Cmd-K-Overlay).
  //   - Mit Query: flache, gescorte Treffer-Liste (Pages + Sensoren +
  //     Aktoren) ueber search-score.ts. Match auf Label/Aliase/Detail
  //     inklusive Fuzzy bei Vertippern.
  //
  // Diese Komponente rendert NUR den Inhalt; Positionierung +
  // Outside-Click + Tastatur uebernimmt der Aufrufer (TopBar.svelte).

  import { getAktorenSummary } from "./aktoren-summary.svelte";
  import { beobachteInventar, inventoryEntries } from "./inventory-index.svelte";
  import { EINSTELLUNGEN } from "./einstellungen/manifest";
  import { semKey } from "./einstellungen/suche";
  import { getKlimaSummary } from "./klima-summary.svelte";
  import { navSections } from "./nav-sections";
  import { router } from "./router.svelte";
  import { routeIntents, sections } from "./routes";
  import { scoreItem, type Scorable } from "./search-score";
  import {
    bonusFor,
    frecentIds,
    recordSelection,
  } from "./search-learn-store.svelte";
  import {
    cosine,
    getEmbedding,
    isAvailable as semanticAvailable,
    queryEmbedding,
  } from "./semantic-search.svelte";
  import { t } from "./i18n.svelte";
  import { suchPfad } from "./gedaechtnis/gedaechtnis";
  import {
    DASHBOARD_PRESETS,
    getActiveDashboardId,
    setActiveDashboardId,
  } from "./dashboards.svelte";
  import {
    getPerspectiveTone,
    type PerspectiveTone,
  } from "./perspective-status.svelte";

  type Props = {
    query: string;
    active: number;
    onSelect: (href: string) => void;
    onHover?: (idx: number) => void;
    onFilteredChange?: (count: number) => void;
  };

  let { query, active, onSelect, onHover, onFilteredChange }: Props = $props();

  // Das Panel ist nur gebaut, solange die Suche offen ist (TopBar:
  // `{#if searchOpen}`, Palette: bei nicht-leerer Eingabe). Zigbee, ESPHome
  // und Automatiken holt sonst niemand im Takt: so lange das Panel steht,
  // sind sie frisch, danach ruhen sie (lib/bei-bedarf.ts).
  $effect(() => beobachteInventar());

  type FlatItem = Scorable & {
    kind:
      | "page"
      | "sensor"
      | "aktor"
      | "task"
      | "automation"
      | "host"
      | "agent"
      | "container"
      | "zigbee"
      | "esphome"
      | "zwave"
      | "einstellung"
      | "gedaechtnis";
    href: string;
    // page = Quell-Page (z.B. "Tasks", "Docker"). Wird zwischen
    // Label und Detail-Badge als subtiler Hint gerendert, damit der
    // User sieht wo das Element herkommt. Pages (kind=="page") haben
    // kein page-Feld -- ihr Section-Name landet eh schon im Badge.
    page?: string;
    // searchKey ist die eindeutige Adresse im Embedding-Cache. Fuer
    // Routen identisch mit href; fuer dynamische Entitaeten (Tasks etc.)
    // ein synthetischer Praefix wie "task:wob-data-backup" (mehrere
    // Tasks zeigen auf dieselbe /tasks-Page, der Embedding-Lookup muss
    // sie aber trennen koennen).
    searchKey?: string;
  };

  type ColItem = { label: string; href: string; active: boolean };
  type SubGroup = { title?: string; items: ColItem[] };
  type ColBlock = { id: string; title: string; href: string; groups: SubGroup[] };

  const klima = $derived(getKlimaSummary());
  const aktoren = $derived(getAktorenSummary());

  // System-Spalte gruppiert in Aufgaben/Diagnose (Nav-Konzept
  // 2026-07-18): eine Sektion "laeuft der Laden?", aber die zwei
  // Denkrichtungen (anstossen vs. hingucken) bleiben ablesbar.
  function buildSystemGroups(items: ColItem[]): SubGroup[] {
    const aufgabenPaths = [
      "/system/tasks",
      "/system/runs",
      "/system/automatiken",
      "/system/backups",
      "/system/archiv",
    ];
    const diagnosePaths = [
      "/system/server",
      "/system/logs",
      "/system/events",
      "/system/meldungen",
      "/system/terminal",
      "/system/db",
      "/system/inferenz",
      "/system/abbild",
      "/system/werkzeuge",
    ];
    const inSet = (paths: string[], it: ColItem) => paths.includes(it.href);
    const aufgaben = items.filter((i) => inSet(aufgabenPaths, i));
    const diagnose = items.filter((i) => inSet(diagnosePaths, i));
    const rest = items.filter(
      (i) => !inSet(aufgabenPaths, i) && !inSet(diagnosePaths, i),
    );
    const out: SubGroup[] = [];
    if (aufgaben.length) out.push({ title: t("cmdk.system.aufgaben", "Aufgaben"), items: aufgaben });
    if (diagnose.length) out.push({ title: t("cmdk.system.diagnose", "Diagnose"), items: diagnose });
    if (rest.length) out.push({ items: rest });
    return out;
  }

  const blocks = $derived.by<ColBlock[]>(() => {
    const cur = router.current;
    const out: ColBlock[] = [];
    for (const ns of navSections) {
      const sec = sections.find((s) => s.title === ns.routesTitle);
      if (!sec) continue;
      const items: ColItem[] = sec.items
        .filter((r) => !r.path.includes(":") && r.inMenu !== false)
        .filter((r) => r.label.toLowerCase() !== ns.label.toLowerCase())
        .map((r) => ({ label: r.label, href: r.path, active: cur === r.path }))
        // Unterpunkte alphabetisch -- vorhersehbar auffindbar statt in
        // routes.ts-Reihenfolge. Bei System greift das INNERHALB der
        // Aufgaben/Diagnose-Gruppen (buildSystemGroups filtert danach).
        .sort((a, b) => a.label.localeCompare(b.label, "de", { sensitivity: "base" }));
      const groups = ns.id === "system" ? buildSystemGroups(items) : [{ items }];
      out.push({ id: ns.id, title: ns.label, href: ns.href, groups });
    }
    return out;
  });

  const dashboardActive = $derived(router.current === "/");

  // Dashboard-Preset-Umschalter (Alles / Infrastruktur / Zuhause) sitzt in
  // der Dashboard-Zeile dieses Panels statt als Tab-Strip auf der Seite --
  // so kostet er keine Seitenhoehe. Auswahl geraetelokal (dashboards.svelte
  // -> localStorage). Klick setzt das Preset UND springt aufs Dashboard.
  const activeDashId = $derived(getActiveDashboardId());
  function pickPreset(id: string): void {
    setActiveDashboardId(id);
    go("/");
  }

  // Status-Ampel je Perspektive: der Punkt neben dem Preset-Label sagt
  // schon VOR dem Wechsel, wo etwas brennt ("Alles" = worst-of).
  function toneDot(tone: PerspectiveTone): string {
    if (tone === "problem") return "bg-danger";
    if (tone === "warning") return "bg-warning";
    return "punkt-ok";
  }
  function toneTitle(tone: PerspectiveTone): string {
    if (tone === "problem")
      return t("dashboard.switcher.tone.problem", "Problem aktiv");
    if (tone === "warning")
      return t("dashboard.switcher.tone.warning", "Warnungen aktiv");
    return t("dashboard.switcher.tone.ok", "alles ruhig");
  }

  // Flat-Suchindex: Pages (mit Aliases) + Live-Sensoren + Aktoren.
  // inMenu:false-Routes (Profil, Settings-Sub-Pages) sind in der
  // Browse-Spalte versteckt, aber hier MUSS sie die Suche finden --
  // sonst sind Login/Push/Allgemein nicht direkt erreichbar.
  const flat = $derived.by<FlatItem[]>(() => {
    const out: FlatItem[] = [];
    for (const sec of sections) {
      for (const r of sec.items) {
        if (r.path.includes(":")) continue;
        out.push({
          kind: "page",
          label: r.label,
          aliases: r.aliases,
          detail: sec.title || t("cmdk.section.page", "Seite"),
          intent: routeIntents[r.path],
          href: r.path,
        });
      }
    }
    if (klima) {
      for (const s of klima.sensors) {
        const base =
          s.domain === "co2"
            ? "/zuhause/co2monitor"
            : s.domain === "lipo"
              ? "/zuhause/lipoguard"
              : s.domain === "waterleak"
                ? "/zuhause/waterleak"
                : "/zuhause/climate";
        out.push({
          kind: "sensor",
          label: s.sensor_id,
          detail: s.domain,
          page: "Sensoren",
          href: `${base}?sensor=${encodeURIComponent(s.sensor_id)}`,
        });
      }
    }
    if (aktoren) {
      for (const d of aktoren.devices) {
        out.push({
          kind: "aktor",
          label: d.name,
          detail: d.kind,
          page: "Aktoren",
          href: `/zuhause/aktoren/${encodeURIComponent(d.name)}`,
        });
      }
    }
    // Homelab-Inventar (Tasks, Automations, Hosts, Agents, Container,
    // Zigbee, ESPHome, Z-Wave). Reaktiv über den inventory-index-Store;
    // solange das Panel offen ist, hält beobachteInventar() die Daten frisch.
    for (const e of inventoryEntries()) {
      out.push({
        kind: e.kind,
        label: e.label,
        aliases: e.aliases,
        detail: e.detail,
        intent: e.intent,
        page: e.page,
        href: e.href,
        searchKey: e.searchKey,
      });
    }
    // Einstellungen: jede Zeile des Einstellungsfensters ist ein Ziel.
    // Seit 04.10.2026 gibt es Anmeldung, Push, Inventar, SSH usw. nicht
    // mehr als eigene Seiten -- wer "Passkey" oder "Inventar" sucht, muss
    // trotzdem ankommen. ?auf=<id> setzt im Fenster die Suche auf den
    // Eintrag und klappt eine Fläche gleich auf. Der Vektor liegt unter
    // demselben Schlüssel wie für die Suche im Fenster (semKey).
    for (const e of EINSTELLUNGEN) {
      out.push({
        kind: "einstellung",
        label: e.name,
        aliases: e.such ?? [],
        detail: e.kategorie,
        intent: e.absicht,
        page: t("einst.titel", "Einstellungen"),
        href: `/einstellungen?auf=${e.id}`,
        searchKey: semKey(e.id),
      });
    }
    return out;
  });

  // Das Nachschieben der Inventar-Eintraege in den Embedding-Index
  // stand frueher hier -- und lief damit erst, wenn dieses Panel
  // gemountet war. Es haengt seit dem 20.08. im Boot (App.svelte), weil
  // die Palette das Panel erst bei nicht-leerer Eingabe mountet und die
  // erste getippte Suche sonst gegen einen halb leeren Index lief.

  // Query-Embedding: debounced fetch. Token-Guard verwirft veraltete
  // Ergebnisse, falls der User schneller tippt als die Anfrage zurueck-
  // kommt. Cancelt sich selbst beim Re-Run / Unmount via cleanup-return.
  let queryEmb = $state<Float32Array | null>(null);
  let queryEmbToken = 0;
  $effect(() => {
    const q = query.trim();
    if (!q || !semanticAvailable()) {
      queryEmb = null;
      return;
    }
    const token = ++queryEmbToken;
    const handle = setTimeout(async () => {
      const emb = await queryEmbedding(q);
      if (token !== queryEmbToken) return;
      queryEmb = emb;
    }, 120);
    return () => clearTimeout(handle);
  });

  // Score-Schwelle fuer rein-semantische Matches (ohne Fuzzy-Hit).
  // Bei bge-m3 sind cosines > 0.45 typischerweise relevant; alles
  // darunter ist Rauschen ("alle Routen sind ein bisschen aehnlich").
  const SEMANTIC_FLOOR = 0.45;

  // Deckel fuer REIN-semantische Treffer (kein Fuzzy, kein Lern-Bonus).
  // bge-m3 hebt bei natuerlichsprachigen Saetzen ("router neu aufsetzen")
  // die Baseline-Aehnlichkeit gegen fast alles -- dann reissen dutzende
  // Items den absoluten Floor und das Panel flutet. Der absolute Floor
  // taugt als Rauschgrenze, nicht als Mengenbremse. Also: Fuzzy-Treffer
  // (exakte Woerter, praezise) bleiben unbegrenzt, die semantischen
  // "Vermutungen" deckeln wir auf die Top-N nach Cosine.
  const SEMANTIC_ONLY_MAX = 8;

  // Mischung aus Fuzzy + Semantic + Lern-Bonus. Fuzzy ist sofort
  // verfuegbar (synchron), Semantic kickt nach ~120 ms + Roundtrip
  // zum Inferenzdienst nach, Lern-Bonus kommt aus search-learn (Schicht 2).
  //
  // Combined-Score: (fuzzy + bonus) normiert auf [0..1] + cosine * 0.6.
  // Der Bonus wird VOR der Normalisierung addiert, damit ein gelernter
  // Treffer relativ zu den anderen Hits skaliert. Items, die nur ueber
  // Lern-Bonus existieren wuerden (kein fuzzy, kein semantic), kommen
  // ebenfalls rein -- das ist der Punkt von Schicht 2 fuer Synonyme
  // ohne semantischen Treffer.
  const filtered = $derived.by<FlatItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flat;
    const tokens = q.split(/\s+/).filter(Boolean);

    type Hit = { item: FlatItem; fuzzy: number; semantic: number };
    const hits: Hit[] = [];
    // Rein-semantische Kandidaten (kein Fuzzy, kein Bonus) sammeln wir
    // getrennt und nehmen nur die Top-SEMANTIC_ONLY_MAX nach Cosine mit
    // -- sonst flutet eine Satz-Query das Panel (siehe Kommentar oben).
    const semOnly: { item: FlatItem; sem: number }[] = [];

    for (const item of flat) {
      const fz = scoreItem(item, tokens);
      let sem = 0;
      if (queryEmb) {
        const re = getEmbedding(item.searchKey ?? item.href);
        if (re && re.length === queryEmb.length) sem = cosine(queryEmb, re);
      }
      const itemId = item.searchKey ?? item.href;
      const bonus = bonusFor(q, itemId);
      if (fz !== null || bonus > 0) {
        // Praeziser Treffer (Wort-Match oder gelernt) -- immer rein.
        hits.push({ item, fuzzy: (fz ?? 0) + bonus, semantic: sem });
      } else if (sem >= SEMANTIC_FLOOR) {
        semOnly.push({ item, sem });
      }
    }

    // Nur die staerksten rein-semantischen Vermutungen dazunehmen.
    semOnly.sort((a, b) => b.sem - a.sem);
    for (const s of semOnly.slice(0, SEMANTIC_ONLY_MAX)) {
      hits.push({ item: s.item, fuzzy: 0, semantic: s.sem });
    }

    if (hits.length === 0) return mitGedaechtnis([]);
    let maxFuzzy = 0;
    for (const h of hits) if (h.fuzzy > maxFuzzy) maxFuzzy = h.fuzzy;
    if (maxFuzzy === 0) maxFuzzy = 1;

    hits.sort((a, b) => {
      const aScore = a.fuzzy / maxFuzzy + a.semantic * 0.6;
      const bScore = b.fuzzy / maxFuzzy + b.semantic * 0.6;
      if (Math.abs(bScore - aScore) > 1e-6) return bScore - aScore;
      return a.item.label.length - b.item.label.length;
    });
    return mitGedaechtnis(hits.slice(0, 50).map((h) => h.item));
  });

  // Ganz unten: dieselbe Frage im Gedächtnis (docs/gedaechtnis.md) --
  // frühere Chats, Merker, Doku, Commits. Nur der Sprung auf die Seite;
  // Treffer zeigt erst die Seite (hinter der PIN), nie die Palette.
  function mitGedaechtnis(liste: FlatItem[]): FlatItem[] {
    const f = query.trim();
    if (f.length < 3) return liste;
    return [
      ...liste,
      {
        kind: "gedaechtnis",
        label: t("cmdk.gedaechtnis", "Im Gedächtnis suchen: „{f}“").replace("{f}", f),
        detail: t("gedaechtnis.title", "Gedächtnis"),
        href: suchPfad(f),
        searchKey: "gedaechtnis:suche",
      },
    ];
  }

  // Frequent-Items bei leerer Suche: Top-N aus dem globalen frecency-
  // Score (Lern-Layer). Bei jeder Auswahl -- egal mit welcher Query --
  // wird der globale Score mitgepflegt. Wir mappen die gelernten IDs
  // zurueck auf FlatItems aus dem flachen Suchindex. IDs ohne Mapping
  // (z.B. ein Task ist weggeloescht worden) werden uebersprungen.
  const FRECENT_LIMIT = 6;
  const frecentItems = $derived.by<FlatItem[]>(() => {
    const ids = frecentIds(FRECENT_LIMIT);
    if (ids.length === 0) return [];
    const idx = new Map<string, FlatItem>();
    for (const it of flat) idx.set(it.searchKey ?? it.href, it);
    const out: FlatItem[] = [];
    for (const id of ids) {
      const it = idx.get(id);
      if (it) out.push(it);
    }
    return out;
  });

  // Eltern brauchen die aktuelle Trefferzahl fuer Tastatur-Navigation.
  $effect(() => {
    onFilteredChange?.(filtered.length);
  });

  // Die Art eines Treffers ist eine Kategorie, kein Zustand: neutral
  // (docs/gestaltung.md). Bis 06.10.2026 trug jede Art eine Zustandsfarbe
  // -- Sensoren grün, Aufgaben gelb wie eine Warnung.
  function kindBadge(_k: FlatItem["kind"]): string {
    return "bg-fg/8 text-fg-muted";
  }

  // pickHref haengt ?q=<filter> nur an, wenn der Aufrufer explizit
  // einen Filter-Wert mitgibt. Frueher floss als Fallback der
  // getippte User-Query durch -- das war ein Bug: wer "docker" tippt
  // und auf die Docker-Page springt, wollte die Page sehen, nicht
  // die Container-Liste nach "docker" gefiltert (die dann leer ist,
  // weil kein Container so heisst). Auto-Filter macht nur Sinn, wenn
  // der getippte Begriff garantiert in der Ziel-Liste vorkommt --
  // das ist genau bei konkreten Entitaeten (Task / Automation /
  // Host / Agent) der Fall, wo qForItem das Label durchreicht.
  // Bestehende Query-Params bleiben erhalten; vorhandenes q= wird
  // ueberschrieben. Encoding via encodeURIComponent (nicht
  // URLSearchParams, weil das Leerzeichen zu '+' macht und der
  // Hash-Router decodeURIComponent benutzt).
  function pickHref(href: string, qOverride?: string): string {
    const q = (qOverride ?? "").trim();
    if (!q) return href;
    const [path, search = ""] = href.split("?");
    const parts: string[] = [];
    for (const pair of search.split("&")) {
      if (!pair || pair === "q" || pair.startsWith("q=")) continue;
      parts.push(pair);
    }
    parts.push("q=" + encodeURIComponent(q));
    return path + "?" + parts.join("&");
  }

  function go(href: string, qOverride?: string, itemId?: string): void {
    // Lern-Event: gelernt wird mit der GETIPPTEN Query (query, nicht
    // qOverride -- letzteres ist ein nachgereichter Filter fuers Ziel).
    // itemId faellt zurueck auf href, wenn der Aufrufer nichts mitgibt.
    recordSelection(query, itemId ?? href);
    onSelect(pickHref(href, qOverride));
  }

  // Nur Entitaeten liefern einen Filter-Wert: ihr Label ist auf der
  // Ziel-Page garantiert als Treffer vorhanden. Pages, Sensoren,
  // Aktoren bekommen keinen Auto-Filter -- der User wollte die Page
  // erreichen, nicht eine Liste mit "keine Treffer".
  function qForItem(it: FlatItem): string | undefined {
    // Nur Kinds, deren Ziel-Page einen ?q=-Filter unterstuetzt,
    // bekommen das Label auto-appended. Z-Wave-Seite hat heute
    // keinen Text-Filter -- Klick navigiert nur hin, fertig.
    if (
      it.kind === "task" ||
      it.kind === "automation" ||
      it.kind === "host" ||
      it.kind === "agent" ||
      it.kind === "container" ||
      it.kind === "zigbee" ||
      it.kind === "esphome"
    ) {
      return it.label;
    }
    return undefined;
  }
</script>

<!-- Kein eigener Hoehen-Deckel mehr: die max-height setzt anchoredPopover
     am Panel-Rahmen aus dem ECHTEN Platz unter der Such-Pille. Die frueher
     hier stehende Schaetzung (calc(100dvh - 8rem)) musste die Bar-Hoehe
     nachbilden und lag bei jeder Aenderung daneben. Hier bleibt nur die
     Rolle im Flex-Panel: darf schrumpfen (min-h-0) und scrollt dann. -->
<div class="min-h-0 flex-1 overflow-y-auto px-3 py-3">
  {#if !query}
    <!-- Dashboard als Promo-Block oberhalb der Sektionen. Rechts der
         Preset-Umschalter (segmented) -- Sprung + Ansichtswahl in einer
         Zeile. Container ist ein <div> (kein <a>), weil die Preset-Buttons
         sonst als interaktive Kinder in einem Link laegen (invalides HTML);
         der Sprung aufs Dashboard haengt jetzt an der Label-Zone. -->
    <div class="mb-3">
      <div
        class="border-border flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border p-3 {dashboardActive
          ? 'bg-fg/10 border-border-strong'
          : ''}"
      >
        <a
          href="#/"
          onclick={(e) => {
            e.preventDefault();
            go("/");
          }}
          class="group hover:text-accent flex min-w-0 items-center gap-3"
        >
          <span
            class="bg-accent/15 text-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12 12 3l9 9M5 10v10h14V10" />
            </svg>
          </span>
          <span class="text-fg group-hover:text-accent text-sm font-medium">
            {t("cmdk.dashboard.title", "Dashboard")}
          </span>
        </a>

        <!-- Preset-Umschalter: waehlt die sichtbaren Sektionen und springt
             aufs Dashboard. Segmented-Optik wie TabBar variant=toggle. -->
        <div
          role="group"
          aria-label={t("dashboard.switcher.aria", "Dashboard wählen")}
          class="border-border bg-bg-muted ml-auto inline-flex shrink-0 gap-0.5 rounded-lg border p-0.5"
        >
          {#each DASHBOARD_PRESETS as p (p.id)}
            {@const tone = getPerspectiveTone(p.id)}
            <button
              type="button"
              aria-pressed={activeDashId === p.id}
              onclick={() => pickPreset(p.id)}
              title={toneTitle(tone)}
              class="flex items-center gap-1.5 rounded-md ctrl-s t-body font-medium transition {activeDashId ===
              p.id
                ? 'surface text-fg shadow-sm'
                : 'text-fg-muted hover:text-fg'}"
            >
              <span
                class="h-1.5 w-1.5 shrink-0 rounded-full {toneDot(tone)}"
                aria-hidden="true"
              ></span>
              {t(p.labelKey, p.labelFallback)}
            </button>
          {/each}
        </div>
        <span class="text-fg-subtle t-meta">/</span>
      </div>
    </div>

    {#if frecentItems.length > 0}
      <div class="mb-3">
        <div
          class="text-fg-muted border-border/60 mb-1.5 border-b px-2 pb-1.5 pt-0.5 t-eyebrow font-semibold"
        >
          {t("cmdk.frecent.title", "Zuletzt")}
        </div>
        <div class="flex flex-wrap gap-1.5">
          {#each frecentItems as it (it.searchKey ?? it.href)}
            <button
              type="button"
              onclick={() =>
                go(it.href, qForItem(it), it.searchKey ?? it.href)}
              class="border-border hover:border-accent/40 text-fg-muted hover:text-fg flex items-center gap-1.5 rounded-md border ctrl-s text-sm transition"
              title={it.detail ?? ""}
            >
              <span class="truncate">{it.label}</span>
              {#if it.page}
                <span class="text-fg-subtle t-meta">{it.page}</span>
              {/if}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    <div class="qs-cols">
      {#each blocks as b (b.id)}
        <div class="qs-block">
          <a
            href={`#${b.href}`}
            onclick={(e) => {
              e.preventDefault();
              go(b.href);
            }}
            class="text-fg-muted hover:text-accent border-border/60 mb-1 flex items-center gap-1.5 border-b px-2 pb-1.5 pt-1 t-eyebrow font-semibold transition"
          >
            {b.title}
            <span aria-hidden="true">›</span>
          </a>
          {#each b.groups as g, gi (gi)}
            {#if g.title}
              <div
                class="text-fg-subtle px-2 pb-0.5 pt-2 t-meta font-medium"
              >
                {g.title}
              </div>
            {/if}
            {#each g.items as r (r.href)}
              <a
                href={`#${r.href}`}
                onclick={(e) => {
                  e.preventDefault();
                  go(r.href);
                }}
                class="text-fg hover:bg-bg-muted flex items-center rounded-md ctrl-s t-body transition {r.active
                  ? 'bg-fg/10 text-fg'
                  : ''}"
              >
                <span class="min-w-0 flex-1 truncate">{r.label}</span>
              </a>
            {/each}
          {/each}
        </div>
      {/each}
    </div>
  {:else if filtered.length === 0}
    <div class="text-fg-subtle px-4 py-6 text-center text-sm">
      {t("cmdk.empty", "Keine Treffer")}
    </div>
  {:else}
    {#each filtered as it, i (it.href + ":" + i)}
      <button
        type="button"
        data-idx={i}
        onclick={() => go(it.href, qForItem(it), it.searchKey ?? it.href)}
        onmouseenter={() => onHover?.(i)}
        class="flex w-full items-center justify-between gap-3 rounded-md ctrl-m text-left text-sm transition {i ===
        active
          ? 'bg-fg/10 text-fg'
          : 'text-fg-muted hover:bg-bg-elev/70'}"
      >
        <span class="min-w-0 flex-1 truncate">{it.label}</span>
        {#if it.page}
          <span class="text-fg-subtle shrink-0 t-meta">{it.page}</span>
        {/if}
        <span
          class="shrink-0 rounded px-1.5 py-0.5 t-eyebrow {kindBadge(it.kind)}"
        >
          {it.detail ?? ""}
        </span>
      </button>
    {/each}
  {/if}
</div>

<style>
  /* CSS-Multi-Column: jede Section ist ein Block, der intern
     zusammenhaengend bleibt; Spalten brechen automatisch. */
  .qs-cols {
    columns: 3;
    column-gap: 12px;
  }
  .qs-block {
    break-inside: avoid;
    margin-bottom: 12px;
    display: block;
  }
  @media (max-width: 720px) {
    .qs-cols {
      columns: 2;
    }
  }
  @media (max-width: 480px) {
    .qs-cols {
      columns: 1;
    }
  }
</style>
